/**
 * Lo que encontró un conteo, otra vez.
 *
 * A count finds what money really is and books the gap: a till's drawer on «Sobrante de caja» or
 * «Faltante de caja», general cash on «Sobrante» or «Faltante de efectivo general», and general
 * cash's first count, where the safe starts, on capital. A correction that changes what should
 * have been in that money before the count does not move the money a second time: the count
 * already found it as it was. It moves what the count found, the other way.
 *
 * Each account once, never past zero on either (`reexpresarEn`): a count that found a shortage and
 * is told more money should have been there finds a bigger shortage, not a negative surplus beside
 * it, and a correction undone puts both accounts back where they were. And the count keeps what it
 * found as it stands (`conteosCorregidos`): what a shortage explained may take, and what «Control»
 * puts on whoever had the money. The owner, 2026-10-15: «please make sure everything ties out
 * every time a correction is made»; the live shop's 12 October corte, corrected that day, had put a
 * shortage general cash's count found on «Sobrante de efectivo general» as C$ -783.
 */

import { centavos, formatMoney, type Settings } from '@abastece/shared';
import { ACCOUNTS, type AccountKey } from '../ledger/accounts.js';
import { credit, debit, type JournalEntry, type JournalLine } from '../ledger/journal.js';
import type { DineroContado, Reader } from '../state.js';

/** Where a count books what it found: over, and short. */
export interface CuentasDelConteo {
  sobra: AccountKey;
  falta: AccountKey;
}

export const DE_LA_CAJA: CuentasDelConteo = { sobra: 'SOBRANTE_CAJA', falta: 'FALTANTE_CAJA' };
export const DEL_GENERAL: CuentasDelConteo = { sobra: 'SOBRANTE_GENERAL', falta: 'FALTANTE_GENERAL' };

/** What a count's own entries put on its accounts: over positive, short negative. */
export function totalEn(asientos: readonly Pick<JournalEntry, 'lineas'>[], cuentas: CuentasDelConteo): number {
  const [sobra, falta] = [ACCOUNTS[cuentas.sobra].code, ACCOUNTS[cuentas.falta].code];
  let total = 0;
  for (const a of asientos) {
    for (const l of a.lineas) {
      if (l.cuenta === sobra) total += l.haber - l.debe;
      if (l.cuenta === falta) total -= l.debe - l.haber;
    }
  }
  return total;
}

/**
 * A count's over or short moved from `antes` to `despues` (positive: over): what it put on its
 * accounts comes off, and what it should have put goes on, each account once. The lines come to
 * `despues - antes` on the credit side.
 */
export function reexpresarEn(cuentas: CuentasDelConteo, antes: number, despues: number, memo = 'Corregido'): JournalLine[] {
  const lineas: JournalLine[] = [];
  const sobra = Math.max(despues, 0) - Math.max(antes, 0);
  const falta = Math.max(-despues, 0) - Math.max(-antes, 0);
  if (sobra > 0) lineas.push(credit(cuentas.sobra, centavos(sobra), memo));
  if (sobra < 0) lineas.push(debit(cuentas.sobra, centavos(-sobra), memo));
  if (falta > 0) lineas.push(debit(cuentas.falta, centavos(falta), memo));
  if (falta < 0) lineas.push(credit(cuentas.falta, centavos(-falta), memo));
  return lineas;
}

/** A count a correction reaches, as the books keep it. */
export interface ConteoAlcanzado {
  id: string;
  /** General cash's first count: it set where the safe starts, against capital. */
  inicial: boolean;
  cuentas: CuentasDelConteo;
  /** What it found as it stands, over positive. */
  total: number;
  /** What the owner explained of its shortage, voided explanations aside. */
  explicado: number;
}

/** What it found as it stands: as the owner's corrections left it, or as its own entries say. */
export async function totalDelConteo(read: Reader, config: Settings, id: string, cuentas: CuentasDelConteo): Promise<number> {
  return config.conteosCorregidos[id] ?? totalEn(await read.asientosDeEvento(id), cuentas);
}

/** What the owner explained of a count's shortage (`faltante.explicado`), voided explanations aside. */
export async function explicadoDe(read: Reader, id: string): Promise<number> {
  return (await read.gastosDeFaltante(id)).reduce((s, g) => s + (g.anulado ? 0 : g.montoCordobas), 0);
}

/**
 * The count that found `de` between two places in the log, both left out (`Reader.primerConteoEntre`),
 * as the books keep it; null when none came between. `hasta`: the correction's own place, so that
 * replaying the log, which holds what came after, finds what the correction found.
 */
export async function conteoAlcanzado(
  read: Reader,
  config: Settings,
  de: DineroContado,
  desde: number,
  hasta: number,
): Promise<ConteoAlcanzado | null> {
  const conteo = await read.primerConteoEntre(de, desde, hasta);
  if (!conteo) return null;
  const cuentas = 'general' in de ? DEL_GENERAL : DE_LA_CAJA;
  return {
    id: conteo.id,
    inicial: conteo.inicial,
    cuentas,
    total: await totalDelConteo(read, config, conteo.id, cuentas),
    explicado: await explicadoDe(read, conteo.id),
  };
}

/** What a correction does to a count it reaches: its lines, and what it found before and after. */
export interface Reencontrado {
  id: string;
  inicial: boolean;
  cuentas: CuentasDelConteo;
  antes: number;
  despues: number;
  lineas: JournalLine[];
}

/** A shortage left smaller than what the owner already explained of it. */
export interface ExplicadoDeMas {
  rechazo: string;
  explicado: number;
  faltaria: number;
}

/** Refused when what a count is left short of is less than what the owner explained of it. */
export function explicadoDeMas(faltante: number, explicado: number, cual: string): ExplicadoDeMas | null {
  if (explicado <= faltante) return null;
  return {
    rechazo: `Ya se explicaron ${formatMoney(centavos(explicado))} de lo que faltó en ${cual}; corregido, faltarían ${formatMoney(centavos(faltante))}. Anule esa explicación primero`,
    explicado,
    faltaria: faltante,
  };
}

/**
 * `valor` more (negative: less) should have been in the money `conteo` found since: what it found
 * moves by `-valor`. General cash's first count set where the safe starts, so what it found is
 * capital. Refused when the shortage left is smaller than what the owner explained of it.
 */
export function reencontrar(conteo: ConteoAlcanzado, valor: number, cual: string, memo: string): Reencontrado | ExplicadoDeMas {
  const despues = conteo.total - valor;
  if (conteo.inicial) {
    const lineas = valor > 0 ? [debit('CAPITAL', centavos(valor), memo)] : valor < 0 ? [credit('CAPITAL', centavos(-valor), memo)] : [];
    return { id: conteo.id, inicial: true, cuentas: conteo.cuentas, antes: conteo.total, despues, lineas };
  }
  const no = explicadoDeMas(Math.max(-despues, 0), conteo.explicado, cual);
  if (no) return no;
  return { id: conteo.id, inicial: false, cuentas: conteo.cuentas, antes: conteo.total, despues, lineas: reexpresarEn(conteo.cuentas, conteo.total, despues, memo) };
}

/** What a count found as it stands, for `conteosCorregidos`: only what moved, and never general cash's first count. */
export function corregidos(...reencontrados: (Reencontrado | null | undefined)[]): Record<string, number> {
  const out: Record<string, number> = {};
  for (const r of reencontrados) if (r && !r.inicial && r.despues !== r.antes) out[r.id] = r.despues;
  return out;
}
